import { Card } from './card';

export interface FamiliarityRecord {
  id?: string;
  user_id?: string;
  word_id?: string;
  level: number;
  note?: string;
  created_at?: string;
}

export interface Word {
  id: string;
  term: string;
  meaning?: string;
  language?: string;
  lemma?: string;
  display_term?: string;
  phonetic?: string;
  phonetics?: Record<string, any>;
  pronunciations?: any[];
  audio?: any[];
  parts_of_speech?: string[];
  senses?: any[];
  definitions?: string[];
  translations?: string[];
  examples?: any[]; // can be string[] or Array<{ text: string, translation?: string }>
  forms?: Record<string, any>;
  morphology?: string;
  etymology?: string;
  roots?: string[];
  prefixes?: string[];
  suffixes?: string[];
  syllables?: string[];
  stress?: string;
  collocations?: string[];
  phrases?: string[];
  idioms?: string[];
  phrasal_verbs?: string[];
  synonyms?: string[];
  antonyms?: string[];
  related_words?: string[];
  word_family?: string[];
  confusables?: string[];
  homophones?: string[];
  spelling_variants?: string[];
  usage_notes?: string;
  grammar?: string;
  frequency?: number;
  cefr?: string;
  difficulty?: number;
  exam_tags?: string[];
  topics?: string[];
  domains?: string[];
  register?: string;
  regions?: string[];
  tags?: string[];
  images?: any[];
  videos?: any[];
  media?: any[];
  mnemonics?: string[];
  notes?: string;
  personal?: string;
  external_ids?: Record<string, any>;
  sources?: any[];
  license?: string;
  attribution?: string;
  provenance?: string;
  raw?: any;
  extra?: Record<string, any>;
  schema_version?: string;
  deck_id?: string;
  deck_ids?: string[];
  deck_name?: string;
  familiarity?: number | FamiliarityRecord[];
  familiarity_note?: string;
  created_at?: string;
  updated_at?: string;
  version?: number;
  [key: string]: any;
}

export interface WordDetail extends Word {
  cards?: Card[];
  familiarity?: FamiliarityRecord[];
  deck_ids?: string[];
}

export interface WordsListResponse {
  items: Word[];
  total: number;
}

export interface WordPayload {
  term: string;
  meaning?: string;
  language?: string;
  lemma?: string;
  display_term?: string;
  phonetic?: string;
  phonetics?: Record<string, any>;
  parts_of_speech?: string[];
  examples?: any[];
  tags?: string[];
  cefr?: string;
  roots?: string[];
  synonyms?: string[];
  antonyms?: string[];
  collocations?: string[];
  mnemonics?: string[];
  notes?: string;
  deck_id?: string;
  extra?: Record<string, any>;
  [key: string]: any;
}

export interface FamiliarityPayload {
  level: number; // 0..5
  note?: string;
}

/** Safely extracts printable text from string or object examples/definitions */
export function formatExampleText(ex: any): string {
  if (!ex) return '';
  if (typeof ex === 'string') return ex;
  if (ex && typeof ex === 'object') {
    if (ex.definition) return ex.translation ? `${ex.definition} (${ex.translation})` : ex.definition;
    if (ex.meaning) return ex.meaning;
    if (ex.text && ex.translation) return `${ex.text} (${ex.translation})`;
    if (ex.text) return ex.text;
    if (ex.en && ex.zh) return `${ex.en} (${ex.zh})`;
    if (ex.sentence) return ex.sentence;
    try {
      return JSON.stringify(ex);
    } catch {
      return String(ex);
    }
  }
  return String(ex ?? '');
}

/** Extracts the latest numerical familiarity score (0-5) */
export function getWordFamiliarityLevel(word: Word): number | null {
  if (typeof word.familiarity === 'number') return word.familiarity;
  if (Array.isArray(word.familiarity) && word.familiarity.length > 0) {
    return word.familiarity[0]?.level ?? null;
  }
  return null;
}
